import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import * as moment from 'moment-timezone';
import { ordersData } from '@app/shared/data/orders.data';
import { of } from 'rxjs/internal/observable/of';
import { OrderNote } from '../models/order-note.model';

/**
 * OrdersService
 */
@Injectable({ providedIn: 'root' })
export class OrdersService {
  /**
   * url
   */
  public url;
  /**
   * fileString
   */
  public fileString: any;
  /**
   * orderNum
   */
  private orderNum: any;
  /**
   * uploadSuccess
   * @type {boolean}
   */
  public uploadSuccess = false;

  /**
   * constructor
   * @param {Router} router
   * @param {ActivatedRoute} route
   * @param {HttpClient} http
   * @param {ApiService} apiService
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiService: ApiService,
  ) {}

  /**
   * Gets all orders based on location ID for logged in user
   * @param location
   */
  getOrders(location): Observable<any> {
    // let orders = ordersData;
    // return of(orders);
    this.url = environment.apiUrl + environment.orderStatusURL;
    return this.http.get(this.url + location);
  }

  /**
   * postCancelOrder
   * @param orderNumber
   * @param action
   * @param actionReason
   * @param actionComments
   * @returns {Observable<any>}
   */
  postCancelOrder(orderNumber, action, actionReason, actionComments): Observable<any> {
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    let params = `{
      "orderId": "${orderNumber}",
      "action": "${action}",
      "actionReason": "${actionReason}",
      "actionComments": "${actionComments}"
    }`;
    return this.http.post(environment.apiUrl + environment.orderStatusURL, params, options).pipe(
      catchError((error: any) => throwError(error.error || 'Server error')),
      map((res) => {
        let response = res;
        return response;
      }),
    );
  }

  /**
   * makeFileRequest
   * @param {File} file
   * @param orderNumber
   * @returns {Observable<any>}
   */
  public makeFileRequest(file: File, orderNumber: any): Observable<any> {
    this.orderNum = orderNumber;
    let myReader: FileReader = new FileReader();
    myReader.onloadend = () => {
      this.fileString = myReader.result;
      this.fileString = this.fileString.substring(this.fileString.indexOf(',') + 1);
    };

    myReader.readAsDataURL(file);
    const data$ = new Observable((observer) => {
      observer.next('selected');
      observer.complete();
    });
    return data$;
  }

  /**
   *
   * @param type
   * @param orderNumber
   * @param frameNotes
   * @param previousNotes
   * @param ftFileName
   * @param fileType
   * @param frameTraceSource
   * @param dbFrameTraceUse
   */
  postFrameTrace(
    type,
    orderNumber,
    frameNotes,
    previousNotes,
    ftFileName,
    fileType?,
    frameTraceSource?: 'ResolveFrametrace' | 'SendFrametrace',
    dbFrameTraceUse?: any,
    frameToCome?: any,
  ): Observable<any> {
    this.orderNum = orderNumber;
    // let headers = new HttpHeaders();
    // headers.append('Content-Type', 'application/json');

    // const browserDateTimeNow = moment.tz(moment.tz.guess()).format('M/DD/yyyy h:mmA z');
    const browserDateTimeNow = Date.now();

    // let options = { headers: headers };
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    let params;
    switch (type) {
      case 'LO': // Lenses Only
        // console.log('// 0 - LENSES ONLY //');
        params = `{
          "orderId": "${this.orderNum}",
          "frameToCome": ${frameToCome},
          "addSInstruction": "${frameNotes === undefined ? '' : frameNotes}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "ftFileUploadDate": null
        }`;
        break;
      case 'PO': // Previous Order w. previousNotes
        // console.log('// 1 - Previous Order w. previousNotes //');
        params = `{
          "orderId": "${this.orderNum}",
          "previousOrder": "${previousNotes}",
          "addSInstruction": "${frameNotes === undefined ? '' : frameNotes}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "ftFileUploadDate": null
        }`;
        break;
      case 'FTC': // Frame to Come
        // console.log('// 2 - FRAME TO COME //');
        params = `{
          "orderId": "${this.orderNum}",
          "skipFrameTrace": false,
          "addSInstruction": "${frameNotes === undefined ? '' : frameNotes}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "frameToCome": true,
          "ftFileUploadDate": null
        }`;
        break;
      case 'framedb':
        if (frameToCome === 'true') {
          // "Ship the frame as Frame to come" radio selected
          params = `{
            "orderId": "${this.orderNum}",
            "frameToCome": true,
            "skipFrameTrace": false,
            "frameTrace": "",
            "ftFileName": "frametracedb.oma",
            "dbFrameTraceUse": true,
            "DBFrameTraceUsedDate": "${Date.now()}"
          }`;
        } else {
          // "Process as Lenses Only with Database Trace" radio selected (default)
          params = `{
            "orderId": "${this.orderNum}",
            "frameToCome": false,
            "skipFrameTrace": false,
            "frameTrace": "",
            "ftFileName": "frametracedb.oma",
            "dbFrameTraceUse": true,
            "DBFrameTraceUsedDate": "${Date.now()}"
          }`;
        }
        break;
      default:
        // console.log('// DEFAULT //');
        params = `{
          "orderId": "${this.orderNum}",
          "frameTrace": "${this.fileString}",
          "addSInstruction": "${frameNotes === undefined ? '' : frameNotes}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "ftFileUploadDate": "${Date.now()}"
        }`;
    }
    if (fileType && frameTraceSource === 'SendFrametrace') {
      if (type === 'framedb') {
        params = `{
          "orderId": "${this.orderNum}",
          "frameTrace": "${this.fileString === undefined ? '' : this.fileString}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "ftFileType": "${fileType}",
          "dbFrameTraceUse": true,
          "DBFrameTraceUsedDate": "${Date.now()}"
        }`;
      } else {
        params = `{
          "orderId": "${this.orderNum}",
          "frameTrace": "${this.fileString === undefined ? '' : this.fileString}",
          "ftFileName": "${ftFileName === undefined ? '' : ftFileName}",
          "ftFileType": "${fileType}",
          "orderFTFileUploadDate": "${Date.now()}"
        }`;
      }
      const path = fileType === 'Benefit Number' || fileType === 'PO Number' ? 'upload' : '';
      this.url = environment.apiUrl + environment.frameTraceURL + path;
      // console.log('// /////////////////////////// //');
      // console.log('// What are we sending to API? SEND FRAMETRACE // - //');
      // console.log('// this.url  - ', this.url);
      // console.log('// ftFileName  - ', ftFileName);
      // console.log('// ftFileType  - ', fileType);
      // console.log('// PAYLOAD  - ', params);
      // console.log('// /////////////////////////// //');
      return this.http.post(this.url, params, options).pipe(
        catchError((error) => {
          this.fileString = undefined;
          return new Observable((subscriber) => subscriber.error(error));
        }),
        map((res) => {
          let content = '';
          this.uploadSuccess = true;
          if (res && res['ErrorMessage']) {
            this.uploadSuccess = false;
            content = 'Error occurred uploading the frame trace for Order ID: ' + this.orderNum;
          }
          this.fileString = undefined;
          return content;
        }),
      );
      // return of({ status: 200 });
    } else {
      this.url = environment.apiUrl + environment.frameTraceURL;
      // console.log('// /////////////////////////// //');
      // console.log('// What are we sending to API? RESOLVE FRAMETRACE // - //');
      // console.log('// this.url  - ', this.url);
      // console.log('// PAYLOAD  - ', params);
      // console.log('// /////////////////////////// //');
      // this.uploadSuccess = true;
      // return of({ status: 200 });
      return this.http.post(this.url, params, options).pipe(
        catchError((error) => {
          this.fileString = undefined;
          return new Observable((subscriber) => subscriber.error(error));
        }),
        map((res) => {
          let content = '';
          this.uploadSuccess = true;
          if (res && res['ErrorMessage']) {
            this.uploadSuccess = false;
            content = 'Error occured uploading the frame trace for Order ID: ' + this.orderNum;
          }
          this.fileString = undefined;
          return content;
        }),
      );
    }
  }

  getOrderMetrics(location, startDate, endDate): Observable<any> {
    this.url = environment.apiUrl + environment.orderMetricsURL + location + '/' + startDate + '/' + endDate;
    return this.http.get(this.url);
  }

  getOrderTrends(location, year): Observable<any> {
    return this.apiService.get(`${environment.orderMetricsURL}trending/${location}/${year}`);
  }

  getFrameTrace(orderNumber) {
    return this.apiService.get('frame-trace/' + orderNumber);
  }

  getOrderNotes(orderNumber: string): Observable<OrderNote[]> {
    return this.http.get<OrderNote[]>(`${environment.apiUrl}${environment.orderStatusURL}ordernotes/${orderNumber}`);
  }

  getOrderReports(startDate: string, endDate: string) {
    return this.apiService.get(`essiloradmin/orders?startDate=${startDate}&endDate=${endDate}`);
  }

  addNote(resp: any) {
    return this.apiService.post('orders/note', resp);
  }

  private getUtcOffset(date) {
    return moment(date).subtract(moment(date).utcOffset(), 'minutes').utc();
  }
}
